import { ComplianceCaseTypeEnum, ProfileRelationshipEnum } from "~/graphql/supergraphEnums";
import {
  type GetSubscriptionAddressQuery,
  type ProfileRelationshipType,
  type ProfileType,
} from "~/graphql/supergraphTypes";
import { CompliancePaymentAccountType } from "../Profiles/BankAccountCard/EditBankAccountAction/types";
import { getProfileName } from "../Profiles/utils";
import {
  BankDetailsComplianceCaseType,
  BankDetailsProfileType,
  Option,
  PaymentInstructionFormValues,
} from "./types";

/** Profile names are stored with whatever spacing was typed, including leading, trailing, and repeated spaces. */
export const normalizeForMatch = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

export const getAvailableProfiles = (
  complianceCase: BankDetailsComplianceCaseType,
): { investor: ProfileType; profiles: ProfileType[] } => {
  const investor = complianceCase?.investingProfile as ProfileType;
  const profiles = complianceCase?.profiles as ProfileType[];
  return { investor, profiles };
};

export const getProfilesWithPaymentAccounts = (
  investor: BankDetailsProfileType | null,
  profiles: BankDetailsProfileType[],
): NonNullable<BankDetailsProfileType>[] => {
  const profilesWithAccounts = profiles.filter(
    (profile): profile is NonNullable<BankDetailsProfileType> =>
      profile != null && profile.paymentAccounts !== null && profile.paymentAccounts.length > 0,
  );

  if (investor != null && investor.paymentAccounts && investor.paymentAccounts.length > 0) {
    return [investor, ...profilesWithAccounts];
  }
  return profilesWithAccounts;
};

export const getUniqueProfilePaymentAccountId = (
  investingProfile: BankDetailsProfileType | null,
  profiles: BankDetailsProfileType[] | null | undefined,
): number | undefined => {
  const ids = new Set<number>();
  const profilesWithAccounts = getProfilesWithPaymentAccounts(
    investingProfile,
    profiles ?? [],
  ).filter((profile): profile is NonNullable<BankDetailsProfileType> => profile != null);

  for (const profile of profilesWithAccounts) {
    for (const paymentAccount of profile.paymentAccounts ?? []) {
      if (paymentAccount?.id != null) {
        ids.add(paymentAccount.id);
      }
    }
  }
  return ids.size === 1 ? [...ids][0] : undefined;
};

export const getDisplayPaymentAccountId = (
  caseType: ComplianceCaseTypeEnum,
  casePaymentAccountId: number | null | undefined,
  investingProfile: BankDetailsProfileType | null,
  profiles: BankDetailsProfileType[] | null | undefined,
): number | undefined => {
  if (casePaymentAccountId) {
    return casePaymentAccountId;
  }
  // Unlinked CO has no case.paymentAccount (no externalArena). Other case types must not
  // inherit a profile-level account that may belong to a different investment account.
  // After clone, one profile can hold several accounts; only a unique id is safe.
  if (caseType !== ComplianceCaseTypeEnum.ClientOnboarding) {
    return undefined;
  }
  return getUniqueProfilePaymentAccountId(investingProfile, profiles);
};

export const findBeneficiaryProfile = (
  parentRelationships: ProfileRelationshipType[] | null,
): boolean => {
  if (!parentRelationships || parentRelationships?.length === 0) {
    return true;
  }

  // If profile only has unassigned relationships, it's a new profile
  return parentRelationships.every(
    (relationship: { relationshipType: string }) =>
      ProfileRelationshipEnum.Unassigned ===
      (relationship.relationshipType as ProfileRelationshipEnum),
  );
};

export const isFatcaCrsProfile = (
  profile:
    | Pick<ProfileType, "parentRelationships">
    | { parentRelationships?: { relationshipType?: string | null }[] | null },
): boolean => {
  // Filter only if ALL relationships are FATCA or CRS (dual roles should be shown)
  if (!profile.parentRelationships || profile.parentRelationships.length === 0) {
    return false;
  }

  return profile.parentRelationships.every(
    (relationship) =>
      relationship?.relationshipType === ProfileRelationshipEnum.FatcaSubstantialOwner ||
      relationship?.relationshipType === ProfileRelationshipEnum.CrsControllingPerson,
  );
};

export const filterFatcaCrsProfiles = (profiles: ProfileType[]) => {
  return profiles.filter((profile) => !isFatcaCrsProfile(profile));
};

export const getProfilesMap = (investor: ProfileType | null, profiles: ProfileType[]) => {
  const map = new Map<string, Option[]>();
  const newProfile =
    profiles
      .filter((profile) => profile !== null)
      .find((profile) => findBeneficiaryProfile(profile.parentRelationships)) ?? null;

  // Filter out the new profile and null profiles from the regular profiles
  const regularProfiles = profiles.filter(
    (profile) => profile !== null && profile !== newProfile,
  );

  const profileOptions = filterFatcaCrsProfiles(regularProfiles)
    .filter((profile): profile is NonNullable<typeof profile> => profile !== null)
    .map((profile) => ({
      id: profile.id,
      name: getProfileName(profile),
    }));

  if (profileOptions.length > 0) {
    map.set("[INVESTMENT ACCOUNT NAME]", profileOptions);
  }

  if (investor) {
    map.set("INVESTMENT ACCOUNT", [
      {
        id: investor.id,
        name: getProfileName(investor),
      },
    ]);
  }

  // Add New Profile section with either the new profile or create option
  map.set("New Profile", [
    newProfile
      ? {
          id: newProfile.id,
          name: getProfileName(newProfile),
        }
      : {
          id: 0,
          name: "+ Create new profile",
        },
  ]);

  return map;
};

export const defaultAccountInitialValues: PaymentInstructionFormValues = {
  accountHolderId: -1,
  bankName: "",
  country: "",
  accountNameMatches: "yes",
  bankAccountName: "",
  explanation: "",
  riskRating: null,
};

export const getAccountInitialValues = (
  paymentAccount: CompliancePaymentAccountType | null,
  isPicr?: boolean,
): PaymentInstructionFormValues => {
  if (paymentAccount) {
    // For PICR cases: payeeName is auto-populated from event at case creation, but profile is not set.
    // Don't use payeeName to determine accountNameMatches for PICR - let user decide.
    // Only if a profile is already selected, we check if payeeName was explicitly set by user.
    const hasProfile = !!paymentAccount.profileId;

    let accountNameMatches: "yes" | "no";
    let bankAccountName: string;

    if (isPicr) {
      if (hasProfile && paymentAccount.payeeName) {
        accountNameMatches = "no";
        bankAccountName = paymentAccount.payeeName;
      } else {
        accountNameMatches = "yes";
        bankAccountName = "";
      }
    } else {
      // Non-PICR: existing logic - use payeeName to determine match
      accountNameMatches = paymentAccount.payeeName ? "no" : "yes";
      bankAccountName = paymentAccount.payeeName ?? "";
    }

    return {
      accountHolderId: paymentAccount.profileId ?? -1,
      bankName: paymentAccount.providerName ?? "",
      country: paymentAccount.bankCountry?.alpha2code ?? "",
      accountNameMatches,
      bankAccountName,
      explanation: paymentAccount.mismatchReason ?? "",
      riskRating: paymentAccount.riskRating?.overriddenRating ?? null,
    };
  }
  return defaultAccountInitialValues;
};

export const buildCheckDisplayOverrideFromSubscriptionAccount = (
  subscriptionAddressResults?: GetSubscriptionAddressQuery,
) => {
  if (!subscriptionAddressResults || !subscriptionAddressResults?.subscriptionById) {
    return undefined;
  }

  const { account, paymentPref } = subscriptionAddressResults.subscriptionById;

  const useAccountMailingAddress =
    paymentPref?.__typename === "CheckPaymentPrefObjectType" &&
    paymentPref?.useAccountMailingAddress;

  const mailingAddress = account?.mailingAddress;

  const subscriptionMailingAddress = mailingAddress
    ? {
        street: mailingAddress.street,
        street2: mailingAddress.street2,
        street3: mailingAddress.street3,
        city: mailingAddress.city,
        state: mailingAddress.state?.code,
        region: mailingAddress.region,
        country: mailingAddress.country?.name,
        postalCode: mailingAddress.postalCode,
        countryId: mailingAddress.country?.id,
        stateId: mailingAddress.state?.id,
      }
    : undefined;

  const checkDisplayOverride: Record<string, unknown> = {};

  if (useAccountMailingAddress && subscriptionMailingAddress) {
    checkDisplayOverride["checkAddressOverride"] = subscriptionMailingAddress;
  }

  let payeeName =
    paymentPref?.__typename === "CheckPaymentPrefObjectType"
      ? paymentPref?.payeeName
      : undefined;

  if (!payeeName) {
    payeeName = account?.name;
  }

  if (payeeName) {
    checkDisplayOverride["checkPayeeOverride"] = payeeName;
  }

  return checkDisplayOverride;
};
